import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { ArrowDown, ArrowUp, ArrowUpDown, Ban as Bandage } from 'lucide-react';
import { translate } from '../../lib/translations';
import { InjuryDescriptionModal } from '../Modals/InjuryDescriptionModal';

const formatShortName = (fullName: string) => {
  const normalized = (fullName || '').trim().replace(/\s+/g, ' ');
  if (!normalized) return '';
  const parts = normalized.split(' ');
  if (parts.length < 2) return normalized;
  const firstInitial = parts[0]?.[0]?.toUpperCase() || '';
  const lastName = parts[parts.length - 1] || '';
  if (!firstInitial || !lastName) return normalized;
  return `${firstInitial}.${lastName}`;
};

interface Goalie {
  playerid: number;
  goaliefullname: string;
  teamabbrevs: string;
  salary: number;
  yearleft: string;
  gamesplayed: number;
  wins: number;
  losses: number;
  shutouts: number;
  savepct: number;
  goals: number;
  assists: number;
  isnhl: boolean;
}

interface OtherTeamsGoalieTableProps {
  goalies: Goalie[];
  language: 'en' | 'fr';
  injuredPlayers?: Array<{playerid: number, description: string}>;
  highlightPlayerId?: number;
  highlightTerm?: string;
  onViewDetails?: (goalie: Goalie) => void;
  // Watchlist props
  watchlistPlayerIds?: Set<number>;
  onAddToWatchlist?: (playerId: number) => void;
  onRemoveFromWatchlist?: (playerId: number) => void;
  watchlistActionInProgress?: boolean;
}

interface Column {
  field: keyof Goalie;
  translationKey: string;
  sortable?: boolean;
}

interface SortConfig {
  field: keyof Goalie;
  direction: 'asc' | 'desc';
}

const columns: Column[] = [
  { field: 'goaliefullname', translationKey: 'name', sortable: true },
  { field: 'gamesplayed', translationKey: 'gp', sortable: true },
  { field: 'wins', translationKey: 'wins', sortable: true },
  { field: 'losses', translationKey: 'losses', sortable: true },
  { field: 'shutouts', translationKey: 'shutouts', sortable: true },
  { field: 'goals', translationKey: 'goals', sortable: true },
  { field: 'assists', translationKey: 'assists', sortable: true },
  { field: 'salary', translationKey: 'salary', sortable: true },
];

export function OtherTeamsGoalieTable({
  goalies,
  language,
  injuredPlayers = [],
  highlightPlayerId,
  highlightTerm = '',
  onViewDetails,
  watchlistPlayerIds = new Set(),
  onAddToWatchlist = () => {},
  onRemoveFromWatchlist = () => {},
  watchlistActionInProgress = false
}: OtherTeamsGoalieTableProps) {
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    field: 'wins',
    direction: 'desc',
  });
  const [imageErrors, setImageErrors] = useState<{[key: number]: boolean}>({});
  const [showInjuryModal, setShowInjuryModal] = useState(false);
  const [selectedInjury, setSelectedInjury] = useState<{ playerId: number, playerName: string, description: string } | null>(null);
  const highlightRowDesktopRef = useRef<HTMLTableRowElement | null>(null);
  const highlightRowMobileRef = useRef<HTMLTableRowElement | null>(null);

  const [openActionsForId, setOpenActionsForId] = useState<number | null>(null);
  const actionsToggleRef = useRef<HTMLElement | null>(null);
  const actionsMenuRef = useRef<HTMLDivElement | null>(null);
  const [actionsMenuStyle, setActionsMenuStyle] = useState<React.CSSProperties>({});

  // Watchlist handlers
  const handleAddToWatchlistClick = (playerId: number) => {
    onAddToWatchlist(playerId);
  };

  const handleRemoveFromWatchlistClick = (playerId: number) => {
    onRemoveFromWatchlist(playerId);
  };

  const isInWatchlist = (playerId: number) => {
    return watchlistPlayerIds.has(playerId);
  };

  const closeActionsMenu = () => {
    setOpenActionsForId(null);
    actionsToggleRef.current = null;
  };

  const updateActionsMenuPosition = () => {
    const toggleEl = actionsToggleRef.current;
    const menuEl = actionsMenuRef.current;
    if (!toggleEl || !menuEl) return;

    const rect = toggleEl.getBoundingClientRect();
    const padding = 8;

    const menuWidth = Math.max(220, menuEl.offsetWidth || 0);
    const naturalMenuHeight = menuEl.offsetHeight || 0;

    const spaceBelow = window.innerHeight - rect.bottom - padding;
    const spaceAbove = rect.top - padding;
    const openUp = spaceBelow < naturalMenuHeight && spaceAbove > spaceBelow;

    const available = Math.max(140, openUp ? spaceAbove : spaceBelow);
    const maxHeight = Math.min(available, window.innerHeight - padding * 2);
    const visibleHeight = Math.min(naturalMenuHeight || maxHeight, maxHeight);

    let top = openUp ? rect.top - visibleHeight : rect.bottom;
    if (top < padding) top = padding;
    if (top + visibleHeight > window.innerHeight - padding) {
      top = window.innerHeight - padding - visibleHeight;
    }

    let left = rect.left;
    if (left + menuWidth > window.innerWidth - padding) {
      left = window.innerWidth - padding - menuWidth;
    }
    if (left < padding) left = padding;

    setActionsMenuStyle({
      position: 'fixed',
      top,
      left,
      zIndex: 3000,
      maxHeight,
      overflowY: 'auto',
      minWidth: 220,
    });
  };

  useLayoutEffect(() => {
    if (openActionsForId === null) return;
    const raf = requestAnimationFrame(() => {
      updateActionsMenuPosition();
    });
    return () => cancelAnimationFrame(raf);
  }, [openActionsForId]);

  useEffect(() => {
    if (openActionsForId === null) return;

    const onDocPointerDown = (e: PointerEvent) => {
      const target = e.target as Node | null;
      if (!target) return;
      if (actionsMenuRef.current?.contains(target)) return;
      if (actionsToggleRef.current?.contains(target)) return;
      closeActionsMenu();
    };

    const onEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeActionsMenu();
    };

    const onScrollOrResize = () => {
      updateActionsMenuPosition();
    };

    document.addEventListener('pointerdown', onDocPointerDown, true);
    document.addEventListener('keydown', onEscape);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('scroll', onScrollOrResize, true);

    return () => {
      document.removeEventListener('pointerdown', onDocPointerDown, true);
      document.removeEventListener('keydown', onEscape);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('scroll', onScrollOrResize, true);
    };
  }, [openActionsForId]);

  const handleSort = (field: keyof Goalie) => {
    setSortConfig((currentConfig) => ({
      field,
      direction:
        currentConfig.field === field && currentConfig.direction === 'desc'
          ? 'asc'
          : 'desc',
    }));
  };

  const renderSortIcon = (field: keyof Goalie, sortable?: boolean) => {
    if (!sortable) return null;

    if (sortConfig.field !== field) {
      return <ArrowUpDown size={16} className="ms-1 text-muted opacity-50" />;
    }

    return sortConfig.direction === 'asc' ? (
      <ArrowUp size={16} className="ms-1 text-primary" />
    ) : (
      <ArrowDown size={16} className="ms-1 text-primary" />
    );
  };

  const handleImageError = (playerId: number) => {
    setImageErrors(prev => ({...prev, [playerId]: true}));
  };

  const getGoalieImageUrl = (goalie: Goalie) => {
    if (imageErrors[goalie.playerid]) {
      return "https://assets.nhle.com/mugs/nhl/20242025/EDM/8478402.png";
    }
    return `https://assets.nhle.com/mugs/nhl/20242025/${goalie.teamabbrevs}/${goalie.playerid}.png`;
  };

  const isGoalieInjured = (playerId: number) => {
    return injuredPlayers?.some(p => p.playerid === playerId) || false;
  };

  const getInjuryDescription = (playerId: number) => {
    const injury = injuredPlayers?.find(p => p.playerid === playerId);
    return injury ? injury.description : 'Gardien blessé';
  };

  const handleShowInjuryModal = (goalie: Goalie) => {
    setSelectedInjury({
      playerId: goalie.playerid,
      playerName: goalie.goaliefullname,
      description: getInjuryDescription(goalie.playerid)
    });
    setShowInjuryModal(true);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatCompactSalaryValue = (amount: number) => {
    const abs = Math.abs(amount);
    if (abs >= 1_000_000) {
      const millions = amount / 1_000_000;
      const rounded = Math.round(millions * 10) / 10;
      const text = Number.isInteger(rounded) ? rounded.toFixed(0) : rounded.toFixed(1);
      return `$${text}M`;
    }
    if (abs >= 1_000) {
      const thousands = Math.round(amount / 1_000);
      return `$${thousands}K`;
    }
    return `$${Math.round(amount)}`;
  };

  const sortedGoalies = [...goalies].sort((a, b) => {
    const aValue = a[sortConfig.field];
    const bValue = b[sortConfig.field];

    if (typeof aValue === 'string' && typeof bValue === 'string') {
      return sortConfig.direction === 'asc'
        ? aValue.localeCompare(bValue)
        : bValue.localeCompare(aValue);
    }

    return sortConfig.direction === 'asc'
      ? (aValue as number) - (bValue as number)
      : (bValue as number) - (aValue as number);
  });

  // Auto-scroll une fois la liste triée prête
  useEffect(() => {
    const desktopEl = highlightRowDesktopRef.current;
    const mobileEl = highlightRowMobileRef.current;
    const targetEl = (mobileEl && mobileEl.offsetParent !== null) ? mobileEl : desktopEl;

    if (targetEl) {
      const t = setTimeout(() => {
        targetEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }, 50);
      return () => clearTimeout(t);
    }
  }, [highlightPlayerId, sortedGoalies.map(g => g.playerid).join(',')]);

  return (
    <div className="table-responsive otherteams-table-wrapper">
      <table className="table table-hover table-sm align-middle otherteams-table otherteams-goalies">
        <thead>
          <tr>
            <th className="otherteams-th-actions" aria-label="Actions" title="Actions"></th>
            <th>Photo</th>
            {columns.map((column) => (
              <th
                key={column.translationKey}
                onClick={() => column.sortable && handleSort(column.field)}
                style={{ cursor: column.sortable ? 'pointer' : 'default' }}
                className="user-select-none"
              >
                <div className="d-flex align-items-center">
                  {translate(column.translationKey, language)}
                  {renderSortIcon(column.field, column.sortable)}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedGoalies.map((goalie) => (
            <React.Fragment key={goalie.playerid}>
              {/* Desktop/tablet row (regular table) */}
              <tr
                className={`d-none d-md-table-row ${goalie.playerid === highlightPlayerId ? 'table-warning' : ''}`}
                ref={goalie.playerid === highlightPlayerId ? (el) => { highlightRowDesktopRef.current = el; } : undefined}
              >
                <td>
                  <button
                    type="button"
                    className="btn btn-sm btn-primary otherteams-actions-toggle"
                    aria-haspopup="menu"
                    aria-expanded={openActionsForId === goalie.playerid}
                    title={language === 'fr' ? 'Actions' : 'Actions'}
                    onPointerDown={(e) => {
                      e.stopPropagation();
                      actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                      setOpenActionsForId((prev) => (prev === goalie.playerid ? null : goalie.playerid));
                    }}
                  >
                    ⋯
                  </button>
                </td>
                <td className="position-relative otherteams-photo-cell">
                  <img 
                    src={getGoalieImageUrl(goalie)} 
                    alt={goalie.goaliefullname}
                    className="border otherteams-photo"
                    style={{ cursor: 'pointer' }}
                    onError={() => handleImageError(goalie.playerid)}
                    onClick={() => onViewDetails?.(goalie)}
                  />
                  {isGoalieInjured(goalie.playerid) && (
                    <span 
                      className="position-absolute" 
                      style={{ top: '-3px', right: '-3px', cursor: 'pointer' }}
                      onClick={() => handleShowInjuryModal(goalie)}
                    >
                      <Bandage size={14} className="text-danger" />
                    </span>
                  )}
                </td>
                {columns.map((column) => {
                if (column.field === 'goaliefullname') {
                  const fullName = goalie.goaliefullname;
                  const shortName = formatShortName(fullName);
                  const injured = isGoalieInjured(goalie.playerid);

                  // Highlight search term if present
                  const renderName = (name: string) => {
                    if (highlightTerm && name.toLowerCase().includes(highlightTerm.toLowerCase())) {
                      const regex = new RegExp(`(${highlightTerm})`, 'ig');
                      const parts = name.split(regex);
                      return parts.map((part, idx) => part.toLowerCase() === highlightTerm.toLowerCase() ? (
                        <mark key={idx} className="px-0 bg-warning text-dark">{part}</mark>
                      ) : (
                        <React.Fragment key={idx}>{part}</React.Fragment>
                      ));
                    }
                    return name;
                  };

                  return (
                    <td key={column.translationKey}>
                      <div className="d-flex align-items-center gap-1">
                        <span className="otherteams-player-name-full" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(goalie)}>{renderName(fullName)}</span>
                        <span className="otherteams-player-name-short" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(goalie)}>{renderName(shortName)}</span>
                        {injured && (
                          <Bandage 
                            size={14} 
                            className="text-danger ms-1" 
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleShowInjuryModal(goalie)}
                          />
                        )}
                      </div>
                    </td>
                  );
                }

                if (column.field === 'wins') {
                  const compact = `GP ${goalie.gamesplayed}  W ${goalie.wins}  L ${goalie.losses}`;
                  return (
                    <td key={column.translationKey}>
                      <span className="otherteams-goalie-wins-full">{goalie.wins}</span>
                      <span className="otherteams-goalie-wins-compact">{compact}</span>
                    </td>
                  );
                }

                if (column.field === 'salary') {
                  const fullSalary = formatCurrency(goalie.salary);
                  const compactSalary = formatCompactSalaryValue(goalie.salary);
                  return (
                    <td key={column.translationKey}>
                      <span className="otherteams-salary-full">{fullSalary}</span>
                      <span className="otherteams-salary-compact">{compactSalary}</span>
                    </td>
                  );
                }

                let baseContent: React.ReactNode;
                baseContent = goalie[column.field];

                return (
                  <td key={column.translationKey}>
                    <div className="d-flex align-items-center">{baseContent}</div>
                  </td>
                );
                })}
              </tr>

              {/* Mobile row: single horizontally-scrollable strip inside one cell */}
              <tr
                className={`d-table-row d-md-none ${goalie.playerid === highlightPlayerId ? 'table-warning' : ''}`}
                ref={goalie.playerid === highlightPlayerId ? (el) => { highlightRowMobileRef.current = el; } : undefined}
              >
                <td colSpan={2 + columns.length}>
                  <div className="otherteams-row-scroll">
                    <button
                      type="button"
                      className="btn btn-sm btn-primary otherteams-actions-toggle"
                      aria-haspopup="menu"
                      aria-expanded={openActionsForId === goalie.playerid}
                      title={language === 'fr' ? 'Actions' : 'Actions'}
                      onPointerDown={(e) => {
                        e.stopPropagation();
                        actionsToggleRef.current = e.currentTarget as unknown as HTMLElement;
                        setOpenActionsForId((prev) => (prev === goalie.playerid ? null : goalie.playerid));
                      }}
                    >
                      ⋯
                    </button>

                    <span className="position-relative otherteams-photo-cell">
                      <img
                        src={getGoalieImageUrl(goalie)}
                        alt={goalie.goaliefullname}
                        className="border otherteams-photo"
                        style={{ cursor: 'pointer' }}
                        onError={() => handleImageError(goalie.playerid)}
                        onClick={() => onViewDetails?.(goalie)}
                      />
                      {isGoalieInjured(goalie.playerid) && (
                        <span
                          className="position-absolute"
                          style={{ top: '-3px', right: '-3px', cursor: 'pointer' }}
                          onClick={() => handleShowInjuryModal(goalie)}
                        >
                          <Bandage size={14} className="text-danger" />
                        </span>
                      )}
                    </span>

                    <span className="otherteams-player-name-short" style={{ cursor: 'pointer' }} onClick={() => onViewDetails?.(goalie)}>{formatShortName(goalie.goaliefullname)}</span>
                    <span className="otherteams-goalie-wins-compact">{`GP ${goalie.gamesplayed}  W ${goalie.wins}  L ${goalie.losses}`}</span>
                    <span className="otherteams-salary-compact">{formatCompactSalaryValue(goalie.salary)}</span>
                  </div>
                </td>
              </tr>

              {openActionsForId === goalie.playerid &&
                createPortal(
                  <div
                    ref={actionsMenuRef}
                    className="dropdown-menu show"
                    style={actionsMenuStyle}
                    role="menu"
                  >
                    {onViewDetails && (
                      <button
                        type="button"
                        className="dropdown-item"
                        onClick={() => {
                          onViewDetails(goalie);
                          closeActionsMenu();
                        }}
                      >
                        {language === 'fr' ? 'Voir les détails' : 'View details'}
                      </button>
                    )}

                    <button
                      type="button"
                      className="dropdown-item"
                      disabled={watchlistActionInProgress}
                      onClick={() => {
                        if (isInWatchlist(goalie.playerid)) {
                          handleRemoveFromWatchlistClick(goalie.playerid);
                        } else {
                          handleAddToWatchlistClick(goalie.playerid);
                        }
                        closeActionsMenu();
                      }}
                    >
                      {language === 'fr'
                        ? isInWatchlist(goalie.playerid)
                          ? 'Retirer de la liste de surveillance'
                          : 'Ajouter à la liste de surveillance'
                        : isInWatchlist(goalie.playerid)
                          ? 'Remove from watchlist'
                          : 'Add to watchlist'}
                    </button>

                    {isGoalieInjured(goalie.playerid) && (
                      <>
                        <hr className="dropdown-divider" />
                        <button
                          type="button"
                          className="dropdown-item"
                          onClick={() => {
                            handleShowInjuryModal(goalie);
                            closeActionsMenu();
                          }}
                        >
                          {language === 'fr' ? 'Voir la blessure' : 'View injury'}
                        </button>
                      </>
                    )}
                  </div>,
                  document.body
                )}
            </React.Fragment>
          ))}
        </tbody>
      </table>

      <InjuryDescriptionModal
        show={showInjuryModal}
        onClose={() => setShowInjuryModal(false)}
        playerName={selectedInjury?.playerName || ''}
        description={selectedInjury?.description || ''}
        language={language}
      />
    </div>
  );
}